import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  FileImage,
  Loader2,
  X,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

const ViewKycDetails = ({
  baseurl,
  token,
  selectedDriver,
  setSelectedDriver,
  setKycDrivers,
  activeTab,
  updateDriverKycStatus,
  fetchDriverKycDetails,
  handleStepSubmit,
  previewImage,
  setPreviewImage,
  openKycDialogId,
  setOpenKycDialogId,
  AdminId,
  configRows = [],
  chargeRows = [],
}) => {
  const [driverKycStatus, setLocalDriverKycStatus] = useState(null);

  const [loadingAction, setLoadingAction] = useState(null);

  const [showRejectBox, setShowRejectBox] = useState(false);

  const [rejectStep, setRejectStep] = useState("");

  const [rejectComment, setRejectComment] = useState("");

  const [failedImages, setFailedImages] = useState({});

  const normalizeValue = useCallback((value) => {
    return String(value ?? "")
      .trim()
      .toLowerCase();
  }, []);

  const isValidText = useCallback((value) => {
    if (value === null || value === undefined) {
      return false;
    }

    const normalized = String(value).trim().toLowerCase();

    return (
      normalized !== "" &&
      normalized !== "null" &&
      normalized !== "undefined" &&
      normalized !== "no url" &&
      normalized !== "n/a"
    );
  }, []);

  const displayValue = useCallback(
    (value, fallback = "N/A") => {
      return isValidText(value) ? String(value).trim() : fallback;
    },
    [isValidText],
  );

  const isValidImageUrl = useCallback(
    (value) => {
      if (!isValidText(value)) {
        return false;
      }

      const imageUrl = String(value).trim();

      return (
        imageUrl.startsWith("http://") ||
        imageUrl.startsWith("https://") ||
        imageUrl.startsWith("data:image/") ||
        imageUrl.startsWith("blob:") ||
        imageUrl.startsWith("/")
      );
    },
    [isValidText],
  );

  const kycId = useMemo(() => {
    if (!selectedDriver) {
      return null;
    }

    return (
      selectedDriver?.id ||
      selectedDriver?.kycId ||
      selectedDriver?.kyc_id ||
      selectedDriver?.driverId ||
      selectedDriver?.driver_id ||
      null
    );
  }, [
    selectedDriver?.id,
    selectedDriver?.kycId,
    selectedDriver?.kyc_id,
    selectedDriver?.driverId,
    selectedDriver?.driver_id,
  ]);

  const driverIdentifier = useMemo(() => {
    return (
      selectedDriver?.driver_code ||
      selectedDriver?.driverCode ||
      selectedDriver?.driver_id ||
      selectedDriver?.driverId ||
      selectedDriver?.id ||
      ""
    );
  }, [selectedDriver]);

  useEffect(() => {
    setFailedImages({});
    setPreviewImage?.(null);
    setShowRejectBox(false);
    setRejectStep("");
    setRejectComment("");
    setLocalDriverKycStatus(null);
  }, [selectedDriver?.id, selectedDriver?.driver_code, setPreviewImage]);

  const createFallbackImageUrl = useCallback(
    (fileName) => {
      if (!isValidText(baseurl)) {
        return "";
      }

      if (!isValidText(driverIdentifier)) {
        return "";
      }

      return `${String(baseurl).replace(
        /\/+$/,
        "",
      )}/uploads/kyc/${driverIdentifier}_${fileName}`;
    },
    [baseurl, driverIdentifier, isValidText],
  );

  const images = useMemo(() => {
    if (!selectedDriver) {
      return [];
    }

    return [
      {
        key: "profile_photo",
        src:
          selectedDriver?.profile_photo ||
          selectedDriver?.profilePicture ||
          selectedDriver?.profile_picture ||
          createFallbackImageUrl("profile_photo.jpg"),
        label: "Profile Photo",
        field: "profile_photo",
      },
      {
        key: "aadhaar_front",
        src:
          selectedDriver?.aadhaar_front ||
          selectedDriver?.aadharFrontPhoto ||
          selectedDriver?.aadhaarFront ||
          createFallbackImageUrl("aadhaar_front.jpg"),
        label: "Aadhaar Front",
        field: "aadhaar_front",
      },
      {
        key: "aadhaar_back",
        src:
          selectedDriver?.aadhaar_back ||
          selectedDriver?.aadharBackPhoto ||
          selectedDriver?.aadhaarBack ||
          createFallbackImageUrl("aadhaar_back.jpg"),
        label: "Aadhaar Back",
        field: "aadhaar_back",
      },
      {
        key: "pan_front",
        src:
          selectedDriver?.pan_front ||
          selectedDriver?.panCardFrontPhoto ||
          selectedDriver?.panFront ||
          createFallbackImageUrl("pan_front.jpg"),
        label: "PAN Card Front",
        field: "pan_front",
      },
      {
        key: "driving_licence_img",
        src:
          selectedDriver?.driving_licence_img ||
          selectedDriver?.drivingLicenseFrontPhoto ||
          selectedDriver?.drivingLicenceImage ||
          createFallbackImageUrl("dl_front.jpg"),
        label: "Driving Licence",
        field: "driving_licence_img",
      },
      {
        key: "vehicle_name_plate_img",
        src:
          selectedDriver?.vehicle_name_plate_img ||
          selectedDriver?.licensePlatePhoto ||
          selectedDriver?.vehicleNumberPlateImage ||
          createFallbackImageUrl("vehicle_plate.jpg"),
        label: "Number Plate",
        field: "vehicle_name_plate_img",
      },
    ];
  }, [selectedDriver, createFallbackImageUrl]);

  const statusInfo = useMemo(() => {
    const currentStatus =
      driverKycStatus?.status ||
      driverKycStatus?.kycStatus ||
      selectedDriver?.status ||
      selectedDriver?.kycStatus ||
      selectedDriver?.kyc_status ||
      "";

    const normalizedStatus = normalizeValue(currentStatus);

    return {
      currentStatus: displayValue(currentStatus, "Pending"),
      normalizedStatus,
      isApproved: normalizedStatus === "approved",
      isRejected: normalizedStatus === "rejected",
      isPending: !normalizedStatus || normalizedStatus === "pending",
    };
  }, [driverKycStatus, selectedDriver, normalizeValue, displayValue]);

  const selectedDriverCode = useMemo(() => {
    return selectedDriver?.driver_code || selectedDriver?.driverCode || "";
  }, [selectedDriver]);

  const selectedDriverId = useMemo(() => {
    return (
      selectedDriver?.driverId ||
      selectedDriver?.driver_id ||
      selectedDriver?.driver?.id ||
      selectedDriver?.id ||
      ""
    );
  }, [selectedDriver]);

  const isSameDriver = useCallback(
    (row) => {
      if (!row) {
        return false;
      }

      const rowDriverCode =
        row?.driver_code ||
        row?.driverCode ||
        row?.driver?.driver_code ||
        row?.driver?.driverCode ||
        "";

      const rowDriverId =
        row?.driverId ||
        row?.driver_id ||
        row?.driver?.id ||
        row?.driver?.driverId ||
        "";

      const codeMatches =
        isValidText(selectedDriverCode) &&
        isValidText(rowDriverCode) &&
        normalizeValue(rowDriverCode) === normalizeValue(selectedDriverCode);

      const idMatches =
        isValidText(selectedDriverId) &&
        isValidText(rowDriverId) &&
        normalizeValue(rowDriverId) === normalizeValue(selectedDriverId);

      return Boolean(codeMatches || idMatches);
    },
    [selectedDriverCode, selectedDriverId, isValidText, normalizeValue],
  );

  const hasDriverConfig = useMemo(() => {
    if (!Array.isArray(configRows) || configRows.length === 0) {
      return false;
    }

    return configRows.some((config) => isSameDriver(config));
  }, [configRows, isSameDriver]);

  const hasDriverCharges = useMemo(() => {
    if (!Array.isArray(chargeRows) || chargeRows.length === 0) {
      return false;
    }

    return chargeRows.some((charge) => {
      if (!isSameDriver(charge)) {
        return false;
      }

      const hasChargeField =
        charge?.perDayAmount !== undefined ||
        charge?.per_day_amount !== undefined ||
        charge?.dailyAmount !== undefined ||
        charge?.daily_amount !== undefined ||
        charge?.perOrderAmount !== undefined ||
        charge?.per_order_amount !== undefined ||
        charge?.orderAmount !== undefined ||
        charge?.order_amount !== undefined ||
        charge?.charge !== undefined ||
        charge?.charges !== undefined ||
        charge?.amount !== undefined;

      return hasChargeField;
    });
  }, [chargeRows, isSameDriver]);

  const canApproveKyc = hasDriverConfig && hasDriverCharges;

  const shouldShowApproveButton =
    !statusInfo.isApproved && !statusInfo.isRejected;

  const shouldShowRejectButton =
    !statusInfo.isRejected && !statusInfo.isApproved;

  const onSubmitStep = useCallback(
    async (stepNumber, stepData) => {
      if (typeof handleStepSubmit !== "function") {
        return;
      }

      await handleStepSubmit(stepNumber, stepData);
    },
    [handleStepSubmit],
  );

  const closeModel = useCallback(() => {
    setOpenKycDialogId?.(null);
    setSelectedDriver?.(null);
    setPreviewImage?.(null);
    setLocalDriverKycStatus(null);
    setFailedImages({});
    setLoadingAction(null);
    setShowRejectBox(false);
    setRejectStep("");
    setRejectComment("");
  }, [setOpenKycDialogId, setSelectedDriver, setPreviewImage]);

  const handleImageError = useCallback(
    (imageKey) => {
      setFailedImages((previous) => {
        if (previous[imageKey]) {
          return previous;
        }

        return {
          ...previous,
          [imageKey]: true,
        };
      });

      setPreviewImage?.((currentPreview) => {
        if (!currentPreview) {
          return currentPreview;
        }

        return null;
      });
    },
    [setPreviewImage],
  );

  const handleImageClick = useCallback(
    (image) => {
      if (!image?.key) {
        return;
      }

      if (failedImages[image.key]) {
        toast.error(`${image.label} is unavailable or corrupted`);
        return;
      }

      if (!isValidImageUrl(image.src)) {
        toast.error(`${image.label} is not available`);
        return;
      }

      setPreviewImage?.(String(image.src));
    },
    [failedImages, isValidImageUrl, setPreviewImage],
  );

  const handlePreviewClose = useCallback(() => {
    setPreviewImage?.(null);
  }, [setPreviewImage]);

  const handleKycStatusUpdate = async (
    targetKycId,
    status,
    rejectedStep = null,
    comment = "",
  ) => {
    if (!targetKycId) {
      toast.error("KYC ID is missing");
      return false;
    }

    if (status === "Approve" && statusInfo.isApproved) {
      toast.success("KYC is already approved");
      return false;
    }

    if (status === "Approve" && !hasDriverConfig) {
      toast.error("Please add driver config before approving KYC");
      return false;
    }

    if (status === "Approve" && !hasDriverCharges) {
      toast.error("Please add driver charges before approving KYC");
      return false;
    }

    if (typeof updateDriverKycStatus !== "function") {
      toast.error("KYC update service is unavailable");
      return false;
    }

    setLoadingAction(status);

    try {
      const payload = {
        adminId: String(AdminId || ""),
        kycId: String(targetKycId),
        kycStatus: status === "Approve" ? "Approved" : "Rejected",
      };

      if (status === "Reject") {
        payload.rejectedSteps = [String(rejectedStep)];
        payload.comments = String(comment || "").trim();
      }

      const result = await updateDriverKycStatus(payload);

      if (result?.responseCode !== "00") {
        throw new Error(result?.responseMessage || "Something went wrong");
      }

      const updatedStatus = status === "Approve" ? "Approved" : "Rejected";

      setLocalDriverKycStatus({
        status: updatedStatus,
      });

      setSelectedDriver?.((previous) => ({
        ...previous,
        status: updatedStatus,
        comments:
          status === "Reject"
            ? String(comment || "").trim()
            : previous?.comments || null,
      }));

      setKycDrivers?.((previousRows) => {
        if (!Array.isArray(previousRows)) {
          return previousRows;
        }

        return previousRows.map((driver) => {
          const sameKyc =
            String(driver?.id || driver?.kycId || "") === String(targetKycId);

          const sameCode =
            isValidText(driver?.driver_code || driver?.driverCode) &&
            normalizeValue(driver?.driver_code || driver?.driverCode) ===
              normalizeValue(selectedDriverCode);

          if (!sameKyc && !sameCode) {
            return driver;
          }

          return {
            ...driver,
            status: updatedStatus,
            comments:
              status === "Reject"
                ? String(comment || "").trim()
                : driver?.comments || null,
          };
        });
      });

      toast.success(
        result?.responseMessage ||
          `KYC ${updatedStatus.toLowerCase()} successfully`,
      );

      if (typeof fetchDriverKycDetails === "function") {
        await fetchDriverKycDetails(false);
      }

      setShowRejectBox(false);
      setRejectStep("");
      setRejectComment("");

      return true;
    } catch (error) {
      console.error("KYC status update error:", error);

      toast.error(
        error?.response?.data?.responseMessage ||
          error?.message ||
          `Failed to ${status} KYC`,
      );

      return false;
    } finally {
      setLoadingAction(null);
    }
  };

  if (!openKycDialogId || !selectedDriver) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4"
      onClick={closeModel}
    >
      <div
        className="relative max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-20 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">KYC Details</h2>

            <p className="mt-0.5 text-sm text-gray-500">
              {displayValue(
                selectedDriver?.driver_code || selectedDriver?.driverCode,
                "Driver code unavailable",
              )}
            </p>
          </div>

          <button
            type="button"
            onClick={closeModel}
            disabled={loadingAction !== null}
            className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Close KYC details"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 sm:p-6">
          {/* Driver and vehicle details */}
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <h3 className="mb-4 text-base font-semibold text-gray-800">
                Driver Details
              </h3>

              <div className="space-y-3">
                <DetailRow
                  label="Driver Code"
                  value={displayValue(
                    selectedDriver?.driver_code || selectedDriver?.driverCode,
                  )}
                />

                <DetailRow
                  label="Name"
                  value={displayValue(
                    selectedDriver?.name || selectedDriver?.fullName,
                  )}
                />

                <DetailRow
                  label="Email"
                  value={displayValue(selectedDriver?.email)}
                />

                <DetailRow
                  label="Phone"
                  value={displayValue(selectedDriver?.phone)}
                />

                <DetailRow
                  label="Aadhaar No."
                  value={displayValue(
                    selectedDriver?.aadhaar_no || selectedDriver?.aadhaarNo,
                  )}
                />

                <DetailRow
                  label="PAN No."
                  value={displayValue(
                    selectedDriver?.pan_no || selectedDriver?.panNo,
                  )}
                />
              </div>
            </section>

            <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <h3 className="mb-4 text-base font-semibold text-gray-800">
                Vehicle Details
              </h3>

              <div className="space-y-3">
                <DetailRow
                  label="Driving Licence"
                  value={displayValue(
                    selectedDriver?.driving_licence ||
                      selectedDriver?.drivingLicence,
                  )}
                />

                <DetailRow
                  label="Transport Type"
                  value={displayValue(
                    selectedDriver?.transport_type_id ||
                      selectedDriver?.transportType,
                  )}
                />

                <DetailRow
                  label="Vehicle Description"
                  value={displayValue(
                    selectedDriver?.transport_description ||
                      selectedDriver?.transportDescription,
                  )}
                />

                <DetailRow
                  label="Licence Plate"
                  value={displayValue(
                    selectedDriver?.licence_plate ||
                      selectedDriver?.licensePlate,
                  )}
                />

                <DetailRow
                  label="City"
                  value={displayValue(selectedDriver?.city)}
                />

                <DetailRow
                  label="Area"
                  value={displayValue(selectedDriver?.area)}
                />
              </div>
            </section>
          </div>

          {/* Images */}
          <section className="mt-6">
            <h3 className="mb-4 text-base font-semibold text-gray-800">
              KYC Documents
            </h3>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {images.map((item) => {
                const imageAvailable =
                  isValidImageUrl(item?.src) && !failedImages[item.key];

                return (
                  <div
                    key={item.key}
                    className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
                  >
                    {imageAvailable ? (
                      <button
                        type="button"
                        className="block w-full"
                        onClick={() => handleImageClick(item)}
                      >
                        <img
                          src={String(item.src)}
                          alt={item.label}
                          loading="lazy"
                          decoding="async"
                          className="h-48 w-full bg-gray-100 object-cover transition hover:scale-[1.02]"
                          onError={() => handleImageError(item.key)}
                        />
                      </button>
                    ) : (
                      <div className="flex h-48 w-full flex-col items-center justify-center bg-gray-100 px-4 text-center">
                        {failedImages[item.key] ? (
                          <>
                            <AlertTriangle
                              size={28}
                              className="text-orange-500"
                            />

                            <p className="mt-2 text-sm font-semibold text-gray-700">
                              Image unavailable
                            </p>

                            <p className="mt-1 text-xs text-gray-500">
                              File may be corrupted or inaccessible.
                            </p>
                          </>
                        ) : (
                          <>
                            <FileImage size={28} className="text-gray-400" />

                            <p className="mt-2 text-sm font-semibold text-gray-700">
                              No image
                            </p>

                            <p className="mt-1 text-xs text-gray-500">
                              Document was not uploaded.
                            </p>
                          </>
                        )}
                      </div>
                    )}

                    <div className="border-t border-gray-100 px-3 py-3 text-center">
                      <p className="text-sm font-medium text-gray-800">
                        {item.label}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Image preview */}
          {previewImage && (
            <div
              className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4"
              onClick={handlePreviewClose}
            >
              <div
                className="relative max-h-[90vh] max-w-5xl overflow-hidden rounded-xl bg-white p-2 shadow-2xl"
                onClick={(event) => event.stopPropagation()}
              >
                <img
                  src={previewImage}
                  alt="KYC document preview"
                  className="max-h-[82vh] max-w-full object-contain"
                  onError={() => {
                    setPreviewImage?.(null);

                    toast.error("This image is corrupted or unavailable");
                  }}
                />

                <button
                  type="button"
                  onClick={handlePreviewClose}
                  className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white text-gray-700 shadow hover:bg-gray-100"
                  aria-label="Close image preview"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Rejection reason */}
          {statusInfo.isRejected && (
            <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-semibold text-red-700">
                Rejection Reason
              </p>

              <p className="mt-1 text-sm text-gray-700">
                {displayValue(
                  selectedDriver?.comments || selectedDriver?.comment,
                  "No reason provided",
                )}
              </p>
            </div>
          )}

          {/* Status and actions */}
          <div className="mt-6 space-y-4">
            <div className="flex flex-wrap items-center justify-center gap-2">
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${
                  statusInfo.isApproved
                    ? "bg-green-100 text-green-700"
                    : statusInfo.isRejected
                      ? "bg-red-100 text-red-700"
                      : "bg-yellow-100 text-yellow-700"
                }`}
              >
                {statusInfo.isApproved ? (
                  <CheckCircle2 size={14} />
                ) : statusInfo.isRejected ? (
                  <XCircle size={14} />
                ) : (
                  <AlertTriangle size={14} />
                )}
                KYC: {statusInfo.currentStatus}
              </span>

              {!statusInfo.isApproved && !statusInfo.isRejected && (
                <>
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold ${
                      hasDriverConfig
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    Config: {hasDriverConfig ? "Added" : "Missing"}
                  </span>

                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold ${
                      hasDriverCharges
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    Charges: {hasDriverCharges ? "Added" : "Missing"}
                  </span>
                </>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3">
              {shouldShowApproveButton && (
                <button
                  type="button"
                  className="inline-flex min-w-[130px] items-center justify-center gap-2 rounded-lg bg-green-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={() => handleKycStatusUpdate(kycId, "Approve")}
                  disabled={loadingAction !== null || !kycId || !canApproveKyc}
                >
                  {loadingAction === "Approve" ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={16} />
                      Approve
                    </>
                  )}
                </button>
              )}

              {shouldShowRejectButton && (
                <button
                  type="button"
                  className="inline-flex min-w-[130px] items-center justify-center gap-2 rounded-lg bg-red-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={() => setShowRejectBox(true)}
                  disabled={loadingAction !== null}
                >
                  <XCircle size={16} />
                  Reject
                </button>
              )}
            </div>

            {shouldShowApproveButton &&
              (!hasDriverConfig || !hasDriverCharges) && (
                <div className="mx-auto max-w-xl rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-center text-xs text-orange-700">
                  {!hasDriverConfig && !hasDriverCharges
                    ? "Driver config and charges are required before approval."
                    : !hasDriverConfig
                      ? "Driver config is required before approval."
                      : "Driver charges are required before approval."}
                </div>
              )}

            {/* Reject box */}
            {showRejectBox && shouldShowRejectButton && (
              <div className="mx-auto w-full max-w-3xl rounded-xl border border-red-200 bg-white p-4 shadow-sm">
                <div className="mb-4 flex items-center justify-between border-b border-red-100 pb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-red-600">
                      Reject KYC
                    </h3>

                    <p className="text-xs text-gray-500">
                      Select the failed document and enter a clear reason.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    onClick={() => {
                      setShowRejectBox(false);
                      setRejectStep("");
                      setRejectComment("");
                    }}
                    aria-label="Close rejection form"
                  >
                    <X size={17} />
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label
                      htmlFor="rejectStep"
                      className="mb-1 block text-xs font-semibold text-gray-700"
                    >
                      Rejection Step
                    </label>

                    <select
                      id="rejectStep"
                      value={rejectStep}
                      onChange={(event) => setRejectStep(event.target.value)}
                      className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400"
                    >
                      <option value="">Select Step</option>

                      {images.map((item, index) => (
                        <option key={item.key} value={String(index + 1)}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="rejectComment"
                      className="mb-1 block text-xs font-semibold text-gray-700"
                    >
                      Reason
                    </label>

                    <input
                      id="rejectComment"
                      type="text"
                      value={rejectComment}
                      onChange={(event) => setRejectComment(event.target.value)}
                      placeholder="Enter rejection reason..."
                      className="h-10 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400"
                    />
                  </div>
                </div>

                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded-lg bg-gray-100 px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200"
                    onClick={() => {
                      setShowRejectBox(false);
                      setRejectStep("");
                      setRejectComment("");
                    }}
                    disabled={loadingAction !== null}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="inline-flex min-w-[140px] items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={loadingAction !== null}
                    onClick={async () => {
                      if (!isValidText(rejectStep)) {
                        toast.error("Please select rejection step");
                        return;
                      }

                      if (!isValidText(rejectComment)) {
                        toast.error("Please enter rejection reason");
                        return;
                      }

                      await handleKycStatusUpdate(
                        kycId,
                        "Reject",
                        rejectStep,
                        rejectComment,
                      );
                    }}
                  >
                    {loadingAction === "Reject" ? (
                      <>
                        <Loader2 size={15} className="animate-spin" />
                        Processing...
                      </>
                    ) : (
                      "Confirm Reject"
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DetailRow = ({ label, value }) => {
  return (
    <div className="grid grid-cols-[125px_minmax(0,1fr)] gap-3 text-sm">
      <span className="font-medium text-gray-700">{label}:</span>

      <span className="break-words text-gray-600">{value}</span>
    </div>
  );
};

export default ViewKycDetails;
